import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    MenuItem,
    Typography,
    Box,
    IconButton,
    Alert,
    CircularProgress,
    ListSubheader,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

// Comprehensive unit types organized by category
const UNIT_TYPES = [
    // Count/Quantity
    { category: 'Count / Quantity', items: [
        { value: 'piece', label: 'Piece (pc)' },
        { value: 'unit', label: 'Unit' },
        { value: 'pair', label: 'Pair' },
        { value: 'dozen', label: 'Dozen (12 pcs)' },
        { value: 'pack', label: 'Pack' },
        { value: 'box', label: 'Box' },
        { value: 'case', label: 'Case' },
        { value: 'set', label: 'Set' },
        { value: 'bundle', label: 'Bundle' },
        { value: 'carton', label: 'Carton' },
        { value: 'crate', label: 'Crate' },
    ]},
    // Weight
    { category: 'Weight', items: [
        { value: 'kg', label: 'Kilogram (kg)' },
        { value: 'g', label: 'Gram (g)' },
        { value: 'mg', label: 'Milligram (mg)' },
        { value: 'lb', label: 'Pound (lb)' },
        { value: 'oz', label: 'Ounce (oz)' },
        { value: 'ton', label: 'Metric Ton' },
    ]},
    // Volume
    { category: 'Volume', items: [
        { value: 'liter', label: 'Liter (L)' },
        { value: 'ml', label: 'Milliliter (ml)' },
        { value: 'gallon', label: 'Gallon' },
        { value: 'quart', label: 'Quart' },
        { value: 'pint', label: 'Pint' },
        { value: 'cup', label: 'Cup' },
        { value: 'fl_oz', label: 'Fluid Ounce (fl oz)' },
    ]},
    // Length
    { category: 'Length', items: [
        { value: 'meter', label: 'Meter (m)' },
        { value: 'cm', label: 'Centimeter (cm)' },
        { value: 'mm', label: 'Millimeter (mm)' },
        { value: 'inch', label: 'Inch (in)' },
        { value: 'foot', label: 'Foot (ft)' },
        { value: 'yard', label: 'Yard (yd)' },
    ]},
    // Area
    { category: 'Area', items: [
        { value: 'sq_meter', label: 'Square Meter (m²)' },
        { value: 'sq_foot', label: 'Square Foot (ft²)' },
        { value: 'sq_yard', label: 'Square Yard (yd²)' },
    ]},
    // Containers
    { category: 'Containers', items: [
        { value: 'bottle', label: 'Bottle' },
        { value: 'can', label: 'Can' },
        { value: 'jar', label: 'Jar' },
        { value: 'bag', label: 'Bag' },
        { value: 'sachet', label: 'Sachet' },
        { value: 'tube', label: 'Tube' },
        { value: 'pouch', label: 'Pouch' },
        { value: 'container', label: 'Container' },
    ]},
    // Paper/Fabric
    { category: 'Paper / Fabric', items: [
        { value: 'roll', label: 'Roll' },
        { value: 'sheet', label: 'Sheet' },
        { value: 'ream', label: 'Ream (500 sheets)' },
        { value: 'spool', label: 'Spool' },
    ]},
    // Pharmacy/Medical
    { category: 'Pharmacy / Medical', items: [
        { value: 'tablet', label: 'Tablet' },
        { value: 'capsule', label: 'Capsule' },
        { value: 'vial', label: 'Vial' },
        { value: 'ampule', label: 'Ampule' },
        { value: 'dose', label: 'Dose' },
        { value: 'strip', label: 'Strip' },
        { value: 'blister', label: 'Blister Pack' },
    ]},
    // Food Service
    { category: 'Food Service', items: [
        { value: 'serving', label: 'Serving' },
        { value: 'portion', label: 'Portion' },
        { value: 'slice', label: 'Slice' },
        { value: 'plate', label: 'Plate' },
        { value: 'bowl', label: 'Bowl' },
    ]},
    // Time-based
    { category: 'Time-based / Service', items: [
        { value: 'hour', label: 'Hour' },
        { value: 'day', label: 'Day' },
        { value: 'session', label: 'Session' },
        { value: 'service', label: 'Service' },
    ]},
];

const ProductDialog = ({ open, product, categories = [], onClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        unitType: '',
        hasVariants: false,
        taxCategory: '',
        variants: []
    });
    
    useEffect(() => {
        if (open) {
            // Clear any previous errors when dialog opens
            setError('');
            
            if (product) {
                // Editing existing product
                // Process variants data - the backend might return null for products without variants
                let processedVariants = [];
                if (product.variants) {
                    if (Array.isArray(product.variants)) {
                        // Filter out any null entries and map to the expected structure
                        processedVariants = product.variants
                            .filter(variant => variant && variant.id)
                            .map(variant => ({
                                id: variant.id,
                                variantName: variant.variant_name || '',
                                attributes: variant.attributes || {}
                            }));
                    } else if (product.variants === null) {
                        // Backend returns null when no variants exist
                        processedVariants = [];
                    }
                }
                
                setFormData({
                    name: product.name || '',
                    description: product.description || '',
                    categoryId: product.category_id || '',
                    unitType: product.unit_type || '',
                    hasVariants: product.has_variants || false,
                    taxCategory: product.tax_category || '',
                    variants: processedVariants
                });
            } else {
                // Creating new product - always reset form
                setFormData({
                    name: '',
                    description: '',
                    categoryId: '',
                    unitType: '',
                    hasVariants: false,
                    taxCategory: '',
                    variants: []
                });
            }
        }
    }, [open, product]);
    
    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'hasVariants' ? checked : value
        }));
    };
    
    const handleVariantChange = (index, field, value) => {
        const newVariants = [...formData.variants];
        newVariants[index] = {
            ...newVariants[index],
            [field]: value
        };
        setFormData(prev => ({ ...prev, variants: newVariants }));
    };
    
    const addVariant = () => {
        setFormData(prev => ({
            ...prev,
            variants: [
                ...prev.variants,
                { variantName: '', attributes: {} }
            ]
        }));
    };
    
    const removeVariant = (index) => {
        setFormData(prev => ({
            ...prev,
            variants: prev.variants.filter((_, i) => i !== index)
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        // Client-side validation
        const trimmedName = formData.name?.trim();
        const trimmedUnitType = formData.unitType?.trim();
        
        if (!trimmedName) {
            setError('Product name is required');
            setLoading(false);
            return;
        }
        
        if (trimmedName.length < 2) {
            setError('Product name must be at least 2 characters long');
            setLoading(false);
            return;
        }
        
        if (!trimmedUnitType) {
            setError('Unit type is required');
            setLoading(false);
            return;
        }
        
        if (!formData.categoryId) {
            setError('Category is required');
            setLoading(false);
            return;
        }
        
        // Validate variants if has variants is enabled
        if (formData.hasVariants) {
            if (!formData.variants || formData.variants.length === 0) {
                setError('At least one variant is required when "has variants" is enabled');
                setLoading(false);
                return;
            }
            
            for (let i = 0; i < formData.variants.length; i++) {
                const variant = formData.variants[i];
                const trimmedVariantName = variant.variantName?.trim();
                
                if (!trimmedVariantName) {
                    setError(`Variant ${i + 1}: Variant name cannot be empty`);
                    setLoading(false);
                    return;
                }
            }
            
            // Check for duplicate variant names
            const variantNames = formData.variants.map(v => v.variantName?.trim().toLowerCase()).filter(Boolean);
            const uniqueNames = new Set(variantNames);
            if (variantNames.length !== uniqueNames.size) {
                setError('Variant names must be unique');
                setLoading(false);
                return;
            }
        }
        
        try {
            const payload = {
                ...formData,
                name: trimmedName,
                unitType: trimmedUnitType,
                description: formData.description?.trim() || '',
                taxCategory: formData.taxCategory?.trim() || '',
                variants: formData.hasVariants ? formData.variants.map(v => ({
                    ...v,
                    variantName: v.variantName?.trim()
                })) : []
            };
            
            let createdProduct = null;
            let isUpdate = false;
            
            if (product) {
                const response = await api.put(`/products/${product.id}`, payload);
                isUpdate = true;
                // For updates, we get both the message and updated product data
                createdProduct = response.data.product;
            } else {
                const response = await api.post('/products', payload);
                createdProduct = response.data.product;
            }
            
            onClose(createdProduct, isUpdate); // Pass product and update flag to parent
        } catch (error) {
            console.error('Error saving product:', error);
            setError(error.response?.data?.message || 'Error saving product');
        } finally {
            setLoading(false);
        }
    };
    
    const handleClose = () => {
        // Call onClose with null to indicate no product was created
        onClose(null);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {product ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
            
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {(!Array.isArray(categories) || categories.length === 0) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Please create at least one category before adding products.
                        </Alert>
                    )}
                    
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                label="Product Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                select
                                label="Category"
                                name="categoryId"
                                value={formData.categoryId}
                                onChange={handleChange}
                                disabled={loading || !Array.isArray(categories) || categories.length === 0}
                            >
                                <MenuItem value="">Select a category</MenuItem>
                                {Array.isArray(categories) && categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.path || category.name}
                                    </MenuItem>
                                ))}
                                {(!Array.isArray(categories) || categories.length === 0) && (
                                    <MenuItem disabled>No categories available</MenuItem>
                                )}
                            </TextField>
                        </Grid>
                        
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                select
                                label="Unit Type"
                                name="unitType"
                                value={formData.unitType}
                                onChange={handleChange}
                                disabled={loading}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            style: {
                                                maxHeight: 400,
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select a unit type</em>
                                </MenuItem>
                                {UNIT_TYPES.map((group) => [
                                    <ListSubheader key={group.category} sx={{
                                        bgcolor: 'grey.100',
                                        fontWeight: 700,
                                        color: 'primary.main',
                                        lineHeight: '36px'
                                    }}>
                                        {group.category}
                                    </ListSubheader>,
                                    ...group.items.map((unit) => (
                                        <MenuItem key={unit.value} value={unit.value} sx={{ pl: 3 }}>
                                            {unit.label}
                                        </MenuItem>
                                    ))
                                ])}
                            </TextField>
                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Tax Category"
                                name="taxCategory"
                                value={formData.taxCategory}
                                onChange={handleChange}
                                placeholder="e.g., standard, reduced, zero"
                            />
                        </Grid>
                        
                        <Grid size={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.hasVariants}
                                        onChange={handleChange}
                                        name="hasVariants"
                                    />
                                }
                                label="This product has variants"
                            />
                        </Grid>
                        
                        {formData.hasVariants && (
                            <Grid size={12}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Product Variants
                                    </Typography>
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addVariant}
                                        sx={{ mb: 2 }}
                                    >
                                        Add Variant
                                    </Button>
                                    
                                    {formData.variants.map((variant, index) => (
                                        <Box
                                            key={variant.id || `new-${index}`}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid size={11}>
                                                    <TextField
                                                        fullWidth
                                                        required
                                                        label="Variant Name"
                                                        value={variant.variantName}
                                                        onChange={(e) => handleVariantChange(index, 'variantName', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={1}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => removeVariant(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        
                    </Grid>
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || !Array.isArray(categories) || categories.length === 0}
                    >
                        {loading ? <CircularProgress size={24} /> : (product ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ProductDialog; 